import { os } from '../../../lib/cep/node';
import { ns, version } from '../../../../shared/shared';
import { csi } from '../../../lib/utils/bolt';
import { ANALYTICS_ENV } from '../../../utils/currentEnv.utils';
import { resolveExchangeIdentity } from './identity.utils';

export const APP_SLUG = 'autocut_exchange';

export type ExchangeContext = Record<string, string | number | boolean>;

export type OsSlug = 'MAC' | 'WINDOWS' | 'UNKNOWN';

/**
 * Same coarse discriminator the desktop app reports (`MAC` / `WINDOWS`), so the
 * two data sets can be compared, but without its throw on an unknown platform.
 */
export const fetchOS = (): OsSlug => {
  try {
    const platform = os.platform();
    if (platform === 'darwin') return 'MAC';
    if (platform === 'win32') return 'WINDOWS';
  } catch {
    /* fall through */
  }
  return 'UNKNOWN';
};

type HostEnvironment = {
  appName?: string;
  appVersion?: string;
  appId?: string;
  appLocale?: string;
  appUILocale?: string;
};

const parseMaybeJson = (value: unknown): unknown => {
  if (typeof value !== 'string') {
    return value;
  }
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
};

/**
 * `getHostEnvironment()` can throw, and in built panels it sometimes comes back
 * as a raw JSON string instead of an object (hence the `__adobe_cep__` retry that
 * `bolt.ts` also uses). Returns undefined rather than throwing.
 */
const getHostEnvironment = (): HostEnvironment | undefined => {
  const candidates: unknown[] = [];

  try {
    candidates.push(csi.getHostEnvironment());
  } catch (error) {
    console.warn('[analytics] csi.getHostEnvironment() failed', error);
  }

  try {
    candidates.push(window.__adobe_cep__?.getHostEnvironment());
  } catch {
    /* ignore */
  }

  for (const candidate of candidates) {
    const parsed = parseMaybeJson(candidate);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as HostEnvironment;
    }
  }

  return undefined;
};

/** "25.1.0" -> 25. Premiere's major version is the useful breakdown. */
const getMajorVersion = (
  appVersion: string | undefined
): number | undefined => {
  if (!appVersion) return undefined;
  const major = Number.parseInt(appVersion.split('.')[0] ?? '', 10);
  return Number.isFinite(major) ? major : undefined;
};

/**
 * `csi.getOSInformation()` caps at "Windows 10" — it string-matches the user
 * agent, and Windows 11 still reports `Windows NT 10`. `os.release()` build
 * numbers are the only way to tell them apart.
 */
const getOsVersionLabel = (osSlug: OsSlug, release: string): string => {
  if (osSlug !== 'WINDOWS') {
    return release ? `macOS (darwin ${release})` : 'macOS';
  }

  const build = Number.parseInt(release.split('.')[2] ?? '', 10);
  if (!Number.isFinite(build)) return 'Windows';
  return build >= 22000 ? 'Windows 11' : 'Windows 10 or older';
};

const safeCall = <T>(fn: () => T): T | undefined => {
  try {
    return fn();
  } catch {
    return undefined;
  }
};

let cachedContext: ExchangeContext | undefined;

/**
 * Base properties attached to every event: app/session discriminators, OS
 * information and the Premiere version. Memoized, and never throws.
 */
export const getExchangeContext = (): ExchangeContext => {
  if (cachedContext) {
    return cachedContext;
  }

  const context: ExchangeContext = {
    app: APP_SLUG,
    environment: ANALYTICS_ENV,
    extension_version: version,
    extension_id: ns,
  };

  try {
    const identity = resolveExchangeIdentity();
    context.is_first_open = identity.isFirstOpen;
    context.open_count = identity.openCount;
    context.distinct_id_source = identity.idSource;
    context.autocut_desktop_present = identity.autocutDesktopPresent;

    const osSlug = fetchOS();
    const release = safeCall(() => os.release()) ?? '';
    context.os = osSlug;
    context.os_platform = safeCall(() => os.platform()) ?? 'unknown';
    context.os_release = release;
    context.os_arch = safeCall(() => os.arch()) ?? 'unknown';
    context.os_version_label = getOsVersionLabel(osSlug, release);
    context.os_info = safeCall(() => csi.getOSInformation()) ?? 'unknown';

    // PostHog's built-in OS breakdowns key off these.
    context.$os =
      osSlug === 'MAC'
        ? 'Mac OS X'
        : osSlug === 'WINDOWS'
        ? 'Windows'
        : 'Unknown';
    context.$os_version = release;

    const hostEnvironment = getHostEnvironment();
    if (hostEnvironment) {
      context.host_app_name = hostEnvironment.appName ?? 'unknown';
      context.host_app_version = hostEnvironment.appVersion ?? 'unknown';
      context.host_app_id = hostEnvironment.appId ?? 'unknown';
      context.host_locale = hostEnvironment.appLocale ?? 'unknown';
      context.host_ui_locale = hostEnvironment.appUILocale ?? 'unknown';

      const major = getMajorVersion(hostEnvironment.appVersion);
      if (major !== undefined) {
        context.host_app_major = major;
      }
    } else {
      context.host_env_unavailable = true;
    }

    const apiVersion = safeCall(() => csi.getCurrentApiVersion());
    if (apiVersion) {
      context.cep_api_version = `${apiVersion.major}.${apiVersion.minor}.${apiVersion.micro}`;
    }

    context.$lib = 'autocut-exchange-cep';
    context.$lib_version = version;
  } catch (error) {
    console.warn('[analytics] getExchangeContext failed', error);
    context.context_incomplete = true;
  }

  cachedContext = context;
  return cachedContext;
};
